import { useEffect, useRef } from 'react';
import { useExtendedSiteSettings } from '@/hooks/useCmsSettings';
import { getPresetById } from '@/lib/theme-presets';

// Convert hex to HSL values for CSS variables
function hexToHsl(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0 0% 0%';

  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Compute relative luminance from hex and return 'light' or 'dark'
function getContrastTextHsl(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0 0% 100%'; // default white

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  // sRGB luminance
  const lum = 0.2126 * (r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4))
            + 0.7152 * (g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4))
            + 0.0722 * (b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4));

  return lum > 0.35 ? '0 0% 7%' : '0 0% 100%';
}

// Darken a hex color by a factor (0..1)
function darkenHex(hex: string, factor: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  const r = Math.round(parseInt(result[1], 16) * (1 - factor));
  const g = Math.round(parseInt(result[2], 16) * (1 - factor));
  const b = Math.round(parseInt(result[3], 16) * (1 - factor));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// Lighten a hex color by mixing with white
function lightenHex(hex: string, factor: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  const r = Math.round(parseInt(result[1], 16) + (255 - parseInt(result[1], 16)) * factor);
  const g = Math.round(parseInt(result[2], 16) + (255 - parseInt(result[2], 16)) * factor);
  const b = Math.round(parseInt(result[3], 16) + (255 - parseInt(result[3], 16)) * factor);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// Generate an SVG favicon with the burst/star icon in the given hex color
function updateFavicon(hex: string) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="64" height="64"><path d="M12 2l1.5 3.5L17 4l-1.5 3.5L19 9l-3.5 1L17 13.5l-3.5-1L12 16l-1.5-3.5L7 13.5l1.5-3.5L5 9l3.5-1L7 4l3.5 1.5L12 2z" fill="${hex}"/><path d="M10 1l.5 1.5L9 2.5l1 .5-.5-2z" fill="${hex}" opacity="0.7"/><path d="M14 1l-.5 1.5 1.5 0-1 .5.5-2z" fill="${hex}" opacity="0.7"/></svg>`;
  const url = 'data:image/svg+xml,' + encodeURIComponent(svg);
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = 'image/svg+xml';
  link.href = url;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { data: settings, isLoading } = useExtendedSiteSettings();
  const themeApplied = useRef(false);

  useEffect(() => {
    if (!settings) return;

    const root = document.documentElement;

    // Resolve colors: use preset as base, then override with individual settings
    const preset = getPresetById((settings as any).theme_preset || 'pink');
    const primaryColor = settings.primary_color || preset?.colors.primary || '#E60073';
    const secondaryColor = settings.secondary_color || preset?.colors.secondary || '#1F1F1F';
    const accentColor = settings.accent_color || preset?.colors.accent || '#C40061';
    const bgColor = settings.background_color || preset?.colors.background || '#FFFFFF';
    const textColor = settings.text_color || preset?.colors.text || '#1F1F1F';
    const linkColor = (settings as any).link_color || preset?.colors.link || accentColor;
    const pillBg = (settings as any).pill_bg_color || preset?.colors.pill_bg || accentColor;
    const pillText = (settings as any).pill_text_color || preset?.colors.pill_text || '#FFFFFF';

    // Apply core colors
    if (primaryColor) {
      const hsl = hexToHsl(primaryColor);
      root.style.setProperty('--primary', hsl);
      root.style.setProperty('--ring', hsl);
      root.style.setProperty('--category-aktualno', hsl);
      root.style.setProperty('--bn-title-hover', hexToHsl(lightenHex(primaryColor, 0.35)));
    }
    if (secondaryColor) {
      const secHsl = hexToHsl(secondaryColor);
      root.style.setProperty('--secondary', secHsl);
    }
    if (accentColor) {
      const accentHsl = hexToHsl(accentColor);
      root.style.setProperty('--accent', accentHsl);
      root.style.setProperty('--bn-accent', accentHsl);
      root.style.setProperty('--sidebar-accent', accentHsl);
      root.style.setProperty('--category-spletne-igre-kvizi', accentHsl);

      // Button contrast-safe
      root.style.setProperty('--bn-button-bg', accentHsl);
      root.style.setProperty('--bn-button-text', getContrastTextHsl(accentColor));
      root.style.setProperty('--bn-button-hover-bg', hexToHsl(darkenHex(accentColor, 0.1)));

      // Header bar in CAPS mode
      root.style.setProperty('--bn-header-bg', accentHsl);
      root.style.setProperty('--bn-header-text', getContrastTextHsl(accentColor));

      // Footer = accent background, contrast text, white hover, dark button
      root.style.setProperty('--bn-footer-bg', accentHsl);
      root.style.setProperty('--bn-footer-text', getContrastTextHsl(accentColor));
      root.style.setProperty('--bn-footer-link-hover', '0 0% 100%');
      root.style.setProperty('--bn-link-hover', accentHsl);
    }
    if (bgColor) {
      root.style.setProperty('--background', hexToHsl(bgColor));
      root.style.setProperty('--card', hexToHsl(bgColor));
      root.style.setProperty('--popover', hexToHsl(bgColor));
    }
    if (textColor) {
      const fgHsl = hexToHsl(textColor);
      root.style.setProperty('--foreground', fgHsl);
      root.style.setProperty('--card-foreground', fgHsl);
      root.style.setProperty('--popover-foreground', fgHsl);
    }
    if (linkColor) {
      root.style.setProperty('--bn-link', hexToHsl(linkColor));
    }

    // Pill / badge colors
    if (pillBg) {
      root.style.setProperty('--accent', hexToHsl(pillBg));
      root.style.setProperty('--bn-accent', hexToHsl(pillBg));
      root.style.setProperty('--bn-button-bg', hexToHsl(pillBg));
      root.style.setProperty('--bn-button-text', getContrastTextHsl(pillBg));
    }

    // Apply fonts
    if (settings.heading_font) {
      root.style.setProperty('--font-heading', `'${settings.heading_font}', Georgia, serif`);
      loadGoogleFont(settings.heading_font);
    }
    if (settings.body_font) {
      root.style.setProperty('--font-body', `'${settings.body_font}', system-ui, sans-serif`);
      loadGoogleFont(settings.body_font);
    }

    // Apply base font size
    if (settings.base_font_size) {
      root.style.setProperty('--font-size-base', `${settings.base_font_size}px`);
    }

    // Apply dark mode if enabled
    if (settings.dark_mode_enabled) {
      root.classList.add('dark-mode-available');
    }

    // Favicon is now a static PNG – no dynamic generation needed

    // Mark theme as applied — reveal content
    themeApplied.current = true;

  }, [settings]);

  // Gate: don't render children until theme colors are applied to prevent FOUC
  // Show a neutral background (matches --background default) with no brand colors
  if (isLoading && !themeApplied.current) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'hsl(0 0% 99%)',
        }}
      />
    );
  }

  return <>{children}</>;
}

// Helper to load Google Fonts dynamically
function loadGoogleFont(fontName: string) {
  const fontId = `google-font-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
  
  // Check if already loaded
  if (document.getElementById(fontId)) return;

  const link = document.createElement('link');
  link.id = fontId;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@300;400;500;600;700&display=swap`;
  document.head.appendChild(link);
}
